<script lang="ts" setup>
import { nextTick, onMounted, reactive, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import BookGridCard from './BookGridCard.vue'
import { api } from '@/api'
import { useElementClick } from '@/composables/tracks/useElementClick'

const props = defineProps<{
  gotoBook: (id: string) => void
}>()

const { elementClick } = useElementClick()

const PAGE_SIZE = 20

// /tag/getAllTag 返回结构：{ id, tagName, isHot }
interface TagItem {
  id: string
  tagName: string
  isHot?: boolean
  [k: string]: any
}

const tags = ref<TagItem[]>([])
const tagLoading = ref(false)
const activeTag = ref<TagItem | null>(null)
// 标签条展开态
const expanded = ref(false)

const listState = reactive({
  items: [] as any[],
  page: 1,
  pages: 0,
  fetching: false,
  ending: false,
  error: null as any,
})

const listRef = ref<HTMLElement | null>(null)

async function fetchTags() {
  tagLoading.value = true
  try {
    const { data } = await api.get('/tag/getAllTag')
    tags.value = data || []
    if (tags.value.length)
      selectTag(tags.value[0], false)
  }
  catch (e) {
    console.error(e)
  }
  finally {
    tagLoading.value = false
  }
}

function resetList() {
  listState.items = []
  listState.page = 1
  listState.pages = 0
  listState.fetching = false
  listState.ending = false
  listState.error = null
}

function selectTag(tag: TagItem, track = true) {
  expanded.value = false
  if (activeTag.value?.id === tag.id)
    return
  if (track)
    elementClick(`tag_${tag.tagName}`)
  activeTag.value = tag
  scrollActiveChipIntoView()
  resetList()
  listRef.value?.scrollTo(0, 0)
  fetchBooks()
}

const chipRowRef = ref<HTMLElement | null>(null)

// 选中标签后，把收起态标签条滚动到当前 chip（尽量居中）
function scrollActiveChipIntoView() {
  nextTick(() => {
    const row = chipRowRef.value
    if (!row)
      return
    const el = row.querySelector<HTMLElement>('.chip.active')
    if (!el)
      return
    const target = el.offsetLeft - (row.clientWidth - el.offsetWidth) / 2
    row.scrollTo({ left: Math.max(0, target), behavior: 'smooth' })
  })
}

async function fetchBooks() {
  const current = activeTag.value
  if (!current || listState.fetching || listState.ending || listState.error)
    return
  listState.fetching = true
  try {
    const { data } = await api.get('/content/getTagContentList', {
      params: {
        tagId: current.id,
        orderBy: 1,
        pageIndex: listState.page,
        pageSize: PAGE_SIZE,
      },
    })
    // 切换标签后返回的旧请求丢弃
    if (activeTag.value?.id !== current.id)
      return
    listState.items = [...listState.items, ...(data.records || [])]
    listState.pages = data.pages || 0
    if (listState.page >= listState.pages)
      listState.ending = true
    else
      listState.page += 1
  }
  catch (e) {
    listState.error = e
  }
  finally {
    listState.fetching = false
  }
}

function retryBooks() {
  listState.error = null
  fetchBooks()
}

function toggleExpand() {
  expanded.value = !expanded.value
  elementClick(expanded.value ? 'tags_expand' : 'tags_collapse')
}

useInfiniteScroll(listRef, async () => {
  await fetchBooks()
}, { distance: 80 })

onMounted(() => {
  fetchTags()
})
</script>

<template>
  <div class="tags-panel relative flex flex-col min-h-0">
    <!-- 标签条：收起态单行横滑 -->
    <div class="chip-bar shrink-0 relative">
      <div v-if="tagLoading" class="py-10px flex justify-center">
        <BasicSpinner />
      </div>
      <template v-else>
        <div ref="chipRowRef" class="chip-row flex items-center overflow-x-auto no-scrollbar px-15px py-10px">
          <div
            v-for="tag in tags"
            :key="tag.id"
            class="chip shrink-0 flex items-center"
            :class="{ active: activeTag?.id === tag.id }"
            @click="selectTag(tag)"
          >
            <img v-if="tag.isHot" src="/images/vowpath/home/icon-hot-a.png" class="chip-hot">
            {{ tag.tagName }}
          </div>
          <!-- 占位，避免最后一个 chip 被展开按钮遮住 -->
          <div class="shrink-0 w-40px h-1px" />
        </div>
        <!-- 右侧渐隐 + 展开按钮 -->
        <div class="chip-fade" />
        <div class="expand-btn" @click="toggleExpand">
          <i-icon-park-outline-down v-if="!expanded" class="w-4 h-4" />
          <i-icon-park-outline-up v-else class="w-4 h-4" />
        </div>
      </template>
    </div>

    <!-- 书籍双列网格 -->
    <div ref="listRef" class="flex-1 min-h-0 overflow-y-auto no-scrollbar px-15px pb-90px">
      <div v-if="listState.error" @click="retryBooks">
        <BizBlockError />
      </div>
      <template v-else-if="listState.items.length">
        <div class="grid grid-cols-2 gap-x-10px gap-y-16px pt-6px">
          <BookGridCard
            v-for="book in listState.items"
            :key="book.id"
            :book="book"
            module-name="tags"
            @click="props.gotoBook"
          />
        </div>
        <div v-if="listState.fetching" class="py-15px flex justify-center">
          <BasicSpinner />
        </div>
        <div v-if="listState.ending" class="py-15px text-12px text-n-text-2 text-center">
          - THE END -
        </div>
      </template>
      <!-- 首页加载中 -->
      <div v-else-if="listState.fetching" class="h-full flex items-center justify-center">
        <BasicSpinner />
      </div>
      <BizBlockEmpty v-else />
    </div>

    <!-- 展开态：多行标签面板 + 遮罩（收起按钮固定右上角，对照蓝湖） -->
    <template v-if="expanded">
      <div class="expand-mask" @click="expanded = false" />
      <div class="expand-panel">
        <div class="flex flex-wrap pl-15px pr-55px pt-10px pb-4px">
          <div
            v-for="tag in tags"
            :key="tag.id"
            class="chip mb-10px flex items-center"
            :class="{ active: activeTag?.id === tag.id }"
            @click="selectTag(tag)"
          >
            <img v-if="tag.isHot" src="/images/vowpath/home/icon-hot-a.png" class="chip-hot">
            {{ tag.tagName }}
          </div>
        </div>
        <div class="expand-collapse-btn" @click="toggleExpand">
          <i-icon-park-outline-up class="w-4 h-4" />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.no-scrollbar {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

/* 标签 chip（对照蓝湖：高30 radius30 深底描边，选中粉描边紫底） */
.chip {
  height: 30px;
  line-height: 28px;
  padding: 0 14px;
  border-radius: 30px;
  background: #2b2c54;
  border: 1px solid #424366;
  color: #dbdae8;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;

  & + .chip {
    margin-left: 10px;
  }

  &.active {
    background: #4e2a5a;
    border-color: #ff5ace;
    color: #ffffff;
  }
}

.expand-panel .chip {
  margin-left: 0;
  margin-right: 10px;
}

/* 右侧渐隐遮罩（对照蓝湖 rgba(32,33,75) 渐变） */
.chip-fade {
  position: absolute;
  right: 40px;
  top: 0;
  bottom: 0;
  width: 20px;
  background: linear-gradient(90deg, rgba(32, 33, 75, 0) 0%, rgba(32, 33, 75, 1) 100%);
  pointer-events: none;
}

/* 热门标签火焰 icon */
.chip-hot {
  width: 12px;
  height: 14px;
  margin-right: 4px;
}

/* 展开/收起圆钮 */
.expand-btn,
.expand-collapse-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #2b2c54;
  border: 1px solid #424366;
  color: #dbdae8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.expand-btn {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
}

/* 展开态收起按钮：固定面板右上角（与收起态按钮同位置） */
.expand-collapse-btn {
  position: absolute;
  right: 15px;
  top: 10px;
}

/* 展开态面板与遮罩 */
.expand-panel {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 20;
  background: var(--vp-tags-panel-bg, #20214b);
}

.expand-mask {
  position: absolute;
  inset: 0;
  z-index: 19;
  background: rgba(0, 0, 0, 0.8);
}
</style>
